import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { presignedUpload, uploadErrorText } from '../../lib/presignedUpload';
import {
  Upload, Camera, Trash2, Eye, Image, Video, X,
  Grid3X3, List, Ban, AlertCircle, CheckCircle, Clock,
  ChevronLeft, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const isVideo = (photo) => {
  return photo.file_type === 'video' || (photo.mime_type && photo.mime_type.startsWith('video/'));
};

/** The served media URL (the API sends `download_url`; `url` is an older shape). */
const mediaUrl = (photo) => photo?.download_url || photo?.url;

const facesOf = (photo) => (Array.isArray(photo?.faces) ? photo.faces : []);
const hasFaceStatus = (photo, status) => facesOf(photo).some((face) => face.status === status);

/**
 * The Photos filters (#900). Each reads the photo's own fields, with the same definitions as the
 * summary counters the server computes (`face_photo_stats`).
 */
const PHOTO_FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'to_review', label: 'Has faces to review', test: (photo) => hasFaceStatus(photo, 'to_review') },
  { key: 'matched', label: 'Matched', test: (photo) => hasFaceStatus(photo, 'approved') },
  { key: 'no_faces', label: 'No faces', test: (photo) => photo.face_detection_status === 'no_faces' },
  { key: 'do_not_use', label: 'Do not use', test: (photo) => Boolean(photo.do_not_use_marketing) },
];

/** The counters when an older API sends no `summary`: the same definitions, counted here. */
const localSummary = (photos) => ({
  photos_uploaded: photos.length,
  faces_detected: photos.reduce((sum, photo) => sum + (Number(photo.detected_faces_count) || 0), 0),
  faces_matched: photos.reduce((sum, photo) => sum + facesOf(photo).filter((face) => face.status === 'approved').length, 0),
  faces_to_review: photos.reduce((sum, photo) => sum + facesOf(photo).filter((face) => face.status === 'to_review').length, 0),
  photos_do_not_use: photos.filter((photo) => photo.do_not_use_marketing).length,
});

const initialsOf = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';

/** A matched contact's avatar on a tile: the photo when there is one, else initials. */
function FaceAvatar({ contact }) {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-emerald-600 text-[9px] font-semibold text-white" title={contact.name}>
      {contact.avatar_url ? <img src={contact.avatar_url} alt="" className="h-full w-full object-cover" /> : initialsOf(contact.name)}
    </span>
  );
}

/**
 * One face on the enlarged photo. A matched face is boxed with its contact's name — only when the
 * server sent the contact, i.e. the viewer may see them (#890). A face still to review is outlined
 * with a Review link to the Face Review tab, but only while the page can open that tab: Face review is paused
 * (#1013), so a viewer with no `onReview` outlines the face and offers no link. Other faces are not marked.
 */
function FaceBox({ face, onReview }) {
  const box = face.bounding_box;
  if (!box || (face.status !== 'approved' && face.status !== 'to_review')) return null;
  const style = { left: `${box.left * 100}%`, top: `${box.top * 100}%`, width: `${box.width * 100}%`, height: `${box.height * 100}%` };
  const approved = face.status === 'approved';
  return (
    <div className={`absolute rounded ${approved ? 'border-2 border-emerald-400' : 'border-2 border-dashed border-amber-400'}`} style={style} data-testid={`face-box-${face.face_index}`}>
      <div className="absolute left-0 top-full mt-1 whitespace-nowrap">
        {approved
          ? <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[11px] font-medium text-white">{face.contact?.name || 'Matched'}</span>
          : onReview && <button type="button" onClick={(e) => { e.stopPropagation(); onReview(face); }} className="rounded bg-amber-500 px-1.5 py-0.5 text-[11px] font-medium text-white hover:bg-amber-600">Review</button>}
      </div>
    </div>
  );
}

export default function EventPhotosGrid({ eventId, readOnlyReason = null, onReviewFace }) {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]); // { file, progress, status }
  const [lightbox, setLightbox] = useState(null); // photo object or null
  const [faceRecognitionAvailable, setFaceRecognitionAvailable] = useState(true);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  /**
   * A Do not use toggle or a delete answers with the server's recount (#900 QA 2026-10-22: the bar
   * kept its load-time figures until a reload). Each write recounts after it commits, so the answer
   * that arrives last carries every write before it and is the one kept, whichever click came first.
   * Without a recount, the bar counts the updated photos itself.
   */
  const applyWriteSummary = (res) => setSummary(res?.data?.summary || null);

  const loadPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/photos`, { headers: getAuthHeader() });
      setPhotos(res.data.data || res.data.photos || []);
      setSummary(res.data.summary || null);
      setFaceRecognitionAvailable(res.data.face_recognition?.available !== false);
    } catch (err) {
      console.error('Failed to load photos:', err);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [eventId, getAuthHeader]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files) => {
    if (readOnlyReason) { toast.info(readOnlyReason); return; }
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);

    const initialUploads = fileList.map((f) => ({
      file: f,
      progress: 0,
      status: 'uploading'
    }));
    setUploads(initialUploads);

    try {
      const urlRes = await axios.post(
        `${API}/events/${eventId}/photos/bulk-upload-urls`,
        { files: fileList.map((f) => ({ file_name: f.name, mime_type: f.type, size: f.size })) },
        { headers: getAuthHeader() }
      );

      const uploadData = urlRes.data;
      let uploaded = 0;
      let firstFailure = null;

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const { upload, s3_key } = uploadData[i];

        try {
          // Presigned POST: the object store refuses a wrong type/size (CASA 5.2.1).
          // fetch() reports no upload progress, so the bar moves on completion.
          await presignedUpload(upload, file);

          setUploads((prev) =>
            prev.map((u, idx) => (idx === i ? { ...u, progress: 1, status: 'registering' } : u))
          );

          await axios.post(
            `${API}/events/${eventId}/photos`,
            { s3_key, original_filename: file.name, mime_type: file.type, file_size: file.size },
            { headers: getAuthHeader() }
          );

          setUploads((prev) =>
            prev.map((u, idx) => (idx === i ? { ...u, status: 'done' } : u))
          );
          uploaded += 1;
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          if (!firstFailure) firstFailure = `${file.name}: ${uploadErrorText(err, 'Upload failed')}`;
          setUploads((prev) =>
            prev.map((u, idx) => (idx === i ? { ...u, status: 'error' } : u))
          );
        }
      }

      // Only claim what actually landed; name the first refusal (CASA 5.2.1
      // added per-file refusals: the object store's 403/400, register's 403/415).
      if (uploaded) toast.success(`Uploaded ${uploaded} file${uploaded > 1 ? 's' : ''}`);
      const failed = fileList.length - uploaded;
      if (failed) {
        toast.error(failed > 1 ? `${failed} files failed. ${firstFailure}` : firstFailure);
      }
      await loadPhotos();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(uploadErrorText(err, 'Failed to start upload'));
      setUploads((prev) => prev.map((u) => ({ ...u, status: 'error' })));
    }

    setTimeout(() => setUploads([]), 3000);
  };

  const handleDelete = async (photoId) => {
    if (readOnlyReason) { toast.info(readOnlyReason); return; }
    if (!window.confirm('Delete this photo?')) return;
    try {
      const res = await axios.delete(`${API}/events/${eventId}/photos/${photoId}`, { headers: getAuthHeader() });
      setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
      applyWriteSummary(res);
      if (lightbox?.photo_id === photoId) setLightbox(null);
      toast.success('Photo deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete photo');
    }
  };

  const toggleDoNotUse = async (photo) => {
    if (readOnlyReason) { toast.info(readOnlyReason); return; }
    const newVal = !photo.do_not_use_marketing;
    try {
      const res = await axios.put(
        `${API}/events/${eventId}/photos/${photo.photo_id}`,
        { do_not_use_marketing: newVal },
        { headers: getAuthHeader() }
      );
      setPhotos((prev) =>
        prev.map((p) =>
          p.photo_id === photo.photo_id ? { ...p, do_not_use_marketing: newVal } : p
        )
      );
      applyWriteSummary(res);
      if (lightbox?.photo_id === photo.photo_id) {
        setLightbox((prev) => ({ ...prev, do_not_use_marketing: newVal }));
      }
      toast.success(newVal ? 'Marked as DO NOT USE' : 'Cleared DO NOT USE flag');
    } catch (err) {
      console.error('Toggle failed:', err);
      toast.error('Failed to update photo');
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  const activeFilter = PHOTO_FILTERS.find((item) => item.key === filter) || PHOTO_FILTERS[0];
  const visiblePhotos = useMemo(() => photos.filter(activeFilter.test), [photos, activeFilter]);
  const counts = summary || localSummary(photos);

  // Lightbox navigation: through the photos the filter shows, by button or arrow key.
  const lightboxIndex = lightbox ? visiblePhotos.findIndex((p) => p.photo_id === lightbox.photo_id) : -1;
  const navigateLightbox = useCallback((dir) => {
    if (lightboxIndex < 0) return;
    const next = lightboxIndex + dir;
    if (next >= 0 && next < visiblePhotos.length) setLightbox(visiblePhotos[next]);
  }, [lightboxIndex, visiblePhotos]);
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') navigateLightbox(-1);
      else if (event.key === 'ArrowRight') navigateLightbox(1);
      else if (event.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lightbox, navigateLightbox]);

  const reviewFace = (face) => {
    setLightbox(null);
    onReviewFace?.(face);
  };

  const photoCount = photos.filter((p) => !isVideo(p)).length;
  const videoCount = photos.filter((p) => isVideo(p)).length;

  const FaceStatusDot = ({ status }) => {
    if (status === 'unsupported') return <span className="w-2 h-2 rounded-full bg-stone-400 inline-block" title="Face recognition is unavailable" />;
    if (status === 'completed') return <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="Face detection completed" />;
    if (status === 'pending' || status === 'processing') return <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" title="Face detection pending" />;
    if (status === 'failed') return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title="Face detection failed" />;
    return null;
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-stone-400">
        <div className="animate-spin w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {readOnlyReason && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note">{readOnlyReason}</div>}
      {!faceRecognitionAvailable && <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600" role="status" data-testid="face-recognition-unavailable">Face recognition is not available for this workspace, so faces are not detected or matched. Photos can still be uploaded, viewed and marked Do not use.</div>}
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {photoCount} Photo{photoCount !== 1 ? 's' : ''}
          {videoCount > 0 && `, ${videoCount} Video${videoCount !== 1 ? 's' : ''}`}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg border transition ${viewMode === 'grid' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}
            title="Grid view"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg border transition ${viewMode === 'list' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50'}`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => !readOnlyReason && fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition text-sm font-medium"
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
        </div>
      </div>

      {/* Summary bar and filters (#900). The counters are the server's, shared with the event page. */}
      {photos.length > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="photos-summary">
            {[
              ['Photos', counts.photos_uploaded],
              ['Faces detected', counts.faces_detected],
              ['Matched', counts.faces_matched],
              ['To review', counts.faces_to_review],
              ['Do not use', counts.photos_do_not_use],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-stone-200 bg-white px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-stone-500">{label}</p>
                <p className="text-xl font-semibold tabular-nums text-stone-900">{value ?? 0}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter photos" data-testid="photos-filters">
            {PHOTO_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={filter === item.key}
                onClick={() => setFilter(item.key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${filter === item.key ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50'}`}
              >
                {item.label} <span className="tabular-nums opacity-70">{photos.filter(item.test).length}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !readOnlyReason && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
          isDragging
            ? 'border-stone-500 bg-stone-100'
            : 'border-stone-200 bg-stone-50 hover:border-stone-300 hover:bg-stone-100'
        }`}
      >
        <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
        <p className="text-sm text-stone-600">Drag & drop photos and videos here, or click to browse</p>
        <p className="text-xs text-stone-400 mt-1">Supports images and videos</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => {
          handleUpload(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div className="space-y-2 bg-white border border-stone-200 rounded-lg p-4">
          <p className="text-sm font-medium text-stone-700">Uploading...</p>
          {uploads.map((u, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-stone-600 truncate w-40">{u.file.name}</span>
              <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    u.status === 'error' ? 'bg-red-500' : u.status === 'done' ? 'bg-green-500' : 'bg-stone-700'
                  }`}
                  style={{ width: `${Math.round(u.progress * 100)}%` }}
                />
              </div>
              <span className="text-xs text-stone-500 w-20 text-right">
                {u.status === 'uploading' && `${Math.round(u.progress * 100)}%`}
                {u.status === 'registering' && 'Saving...'}
                {u.status === 'done' && <span className="text-green-600">Done</span>}
                {u.status === 'error' && <span className="text-red-600">Failed</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {photos.length === 0 && uploads.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-400">
          <Camera className="w-12 h-12 mb-3" />
          <p className="text-lg font-medium text-stone-500" style={{ fontFamily: 'Manrope, sans-serif' }}>No photos yet</p>
          <p className="text-sm mt-1">Upload event photos to get started</p>
        </div>
      )}

      {photos.length > 0 && visiblePhotos.length === 0 && (
        <div className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500" data-testid="photos-filter-empty">
          No photos match “{activeFilter.label}”. <button type="button" className="underline" onClick={() => setFilter('all')}>Show all photos</button>
        </div>
      )}

      {/* Grid view */}
      {visiblePhotos.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="photos-grid">
          {visiblePhotos.map((photo) => (
            <div
              key={photo.photo_id}
              className="group relative bg-white border border-stone-200 rounded-lg overflow-hidden cursor-pointer"
              onClick={() => setLightbox(photo)}
            >
              <div className="aspect-square relative">
                {isVideo(photo) ? (
                  <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                    <Video className="w-10 h-10 text-stone-400" />
                  </div>
                ) : (
                  <img
                    src={mediaUrl(photo)}
                    alt={photo.display_filename || photo.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}

                {/* Video play overlay */}
                {isVideo(photo) && (photo.download_url || photo.url) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                      <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                    </div>
                  </div>
                )}

                {/* DO NOT USE badge */}
                {photo.do_not_use_marketing && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                    DO NOT USE
                  </div>
                )}

                {/* Face detection + count badges */}
                <div className="absolute top-2 right-2 flex items-center gap-1">
                  <FaceStatusDot status={photo.face_detection_status} />
                  {photo.detected_faces_count > 0 && (
                    <span className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      {photo.detected_faces_count} face{photo.detected_faces_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Matched contacts' avatars, and how many faces still need review */}
                {(facesOf(photo).some((face) => face.status === 'approved' && face.contact) || hasFaceStatus(photo, 'to_review')) && (
                  <div className="absolute bottom-8 left-2 right-2 flex items-center justify-between gap-1" data-testid={`photo-faces-${photo.photo_id}`}>
                    <div className="flex -space-x-2">
                      {facesOf(photo).filter((face) => face.status === 'approved' && face.contact).slice(0, 4).map((face) => <FaceAvatar key={face.face_index} contact={face.contact} />)}
                    </div>
                    {hasFaceStatus(photo, 'to_review') && <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-medium text-white">{facesOf(photo).filter((face) => face.status === 'to_review').length} to review</span>}
                  </div>
                )}

                {/* Bottom gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xs truncate max-w-[70%]">
                      {photo.display_filename || photo.original_filename}
                    </span>
                    {photo.photo_number && (
                      <span className="bg-white/20 text-white text-[10px] px-1.5 py-0.5 rounded">
                        #{photo.photo_number}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hover actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleDoNotUse(photo); }} disabled={!!readOnlyReason}
                    className={`p-2 rounded-full transition ${
                      photo.do_not_use_marketing
                        ? 'bg-white text-stone-700 hover:bg-stone-100'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                    title={photo.do_not_use_marketing ? 'Clear DO NOT USE' : 'Mark DO NOT USE'}
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(photo.photo_id); }} disabled={!!readOnlyReason}
                    className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List view */}
      {visiblePhotos.length > 0 && viewMode === 'list' && (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3">File</th>
                <th className="text-left py-2 px-3 hidden sm:table-cell">Type</th>
                <th className="text-left py-2 px-3 hidden md:table-cell">Size</th>
                <th className="text-center py-2 px-3">Do Not Use</th>
                <th className="text-center py-2 px-3 hidden lg:table-cell">Faces</th>
                <th className="text-left py-2 px-3 hidden lg:table-cell">Uploaded By</th>
                <th className="text-left py-2 px-3 hidden md:table-cell">Date</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePhotos.map((photo) => (
                <tr
                  key={photo.photo_id}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition"
                  onClick={() => setLightbox(photo)}
                >
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-stone-100">
                        {isVideo(photo) ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-5 h-5 text-stone-400" />
                          </div>
                        ) : (
                          <img src={mediaUrl(photo)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </div>
                      <span className="text-stone-700 truncate max-w-[200px]">
                        {photo.display_filename || photo.original_filename}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3 hidden sm:table-cell">
                    <span className="text-stone-500 capitalize">{photo.file_type || (isVideo(photo) ? 'video' : 'image')}</span>
                  </td>
                  <td className="py-2 px-3 hidden md:table-cell text-stone-500">{formatSize(photo.file_size)}</td>
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleDoNotUse(photo); }} disabled={!!readOnlyReason}
                      className={`inline-flex items-center justify-center w-8 h-5 rounded-full transition ${
                        photo.do_not_use_marketing
                          ? 'bg-red-500'
                          : 'bg-stone-200'
                      }`}
                      title={photo.do_not_use_marketing ? 'Clear DO NOT USE' : 'Mark DO NOT USE'}
                    >
                      <span
                        className={`block w-3.5 h-3.5 rounded-full bg-white transition transform ${
                          photo.do_not_use_marketing ? 'translate-x-1.5' : '-translate-x-1.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="py-2 px-3 text-center hidden lg:table-cell">
                    <div className="flex items-center justify-center gap-1.5">
                      <FaceStatusDot status={photo.face_detection_status} />
                      {photo.detected_faces_count > 0 && (
                        <span className="text-stone-500 text-xs">{photo.detected_faces_count}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 hidden lg:table-cell text-stone-500">{photo.uploaded_by_name || '-'}</td>
                  <td className="py-2 px-3 hidden md:table-cell text-stone-500 text-xs">{formatDate(photo.created_at)}</td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(photo.photo_id); }} disabled={!!readOnlyReason}
                      className="p-1.5 text-stone-400 hover:text-red-600 rounded transition"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lightbox, portaled to <body> (#496): EventDetail's root is
          `.animate-fade-in`, whose transform would make it the containing
          block for this fixed overlay while the fade runs, and inline it
          would also take the root's `space-y-4` top margin. */}
      {lightbox && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative bg-white rounded-xl overflow-hidden max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-3 right-3 z-10 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Image area */}
            <div className="flex-1 bg-stone-900 flex items-center justify-center relative min-h-[300px]">
              {isVideo(lightbox) ? (
                mediaUrl(lightbox) ? (
                  <video src={mediaUrl(lightbox)} controls className="max-w-full max-h-[70vh]" />
                ) : (
                  <Video className="w-16 h-16 text-stone-500" />
                )
              ) : (
                // The wrapper hugs the image, so a face box in % of the photo lands on the face.
                <div className="relative inline-block" data-testid="lightbox-photo">
                  <img
                    src={mediaUrl(lightbox)}
                    alt={lightbox.display_filename || lightbox.original_filename}
                    className="block max-w-full max-h-[70vh] object-contain"
                  />
                  {facesOf(lightbox).map((face) => <FaceBox key={face.face_index} face={face} onReview={onReviewFace ? reviewFace : undefined} />)}
                </div>
              )}

              {/* Nav arrows */}
              {lightboxIndex > 0 && (
                <button
                  onClick={() => navigateLightbox(-1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {lightboxIndex >= 0 && lightboxIndex < visiblePhotos.length - 1 && (
                <button
                  onClick={() => navigateLightbox(1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Details sidebar */}
            <div className="w-full md:w-72 border-t md:border-t-0 md:border-l border-stone-200 p-5 space-y-4 overflow-y-auto max-h-[40vh] md:max-h-[90vh]">
              <h4 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Photo Details
              </h4>

              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-stone-400 text-xs block">Original filename</span>
                  <span className="text-stone-700 break-all">{lightbox.original_filename}</span>
                </div>
                {lightbox.display_filename && lightbox.display_filename !== lightbox.original_filename && (
                  <div>
                    <span className="text-stone-400 text-xs block">Display filename</span>
                    <span className="text-stone-700 break-all">{lightbox.display_filename}</span>
                  </div>
                )}
                <div>
                  <span className="text-stone-400 text-xs block">File size</span>
                  <span className="text-stone-700">{formatSize(lightbox.file_size)}</span>
                </div>
                {lightbox.uploaded_by_name && (
                  <div>
                    <span className="text-stone-400 text-xs block">Uploaded by</span>
                    <span className="text-stone-700">{lightbox.uploaded_by_name}</span>
                  </div>
                )}
                {lightbox.created_at && (
                  <div>
                    <span className="text-stone-400 text-xs block">Date</span>
                    <span className="text-stone-700">{formatDate(lightbox.created_at)}</span>
                  </div>
                )}
                {lightbox.face_detection_status && (
                  <div>
                    <span className="text-stone-400 text-xs block">Face detection</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <FaceStatusDot status={lightbox.face_detection_status} />
                      <span className="text-stone-700 capitalize">{lightbox.face_detection_status}</span>
                      {lightbox.detected_faces_count > 0 && (
                        <span className="text-stone-500">
                          ({lightbox.detected_faces_count} detected, {lightbox.matched_faces_count || 0} matched)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Who is in this photo: names only for contacts the viewer may see */}
              {facesOf(lightbox).some((face) => face.status === 'approved' || face.status === 'to_review') && (
                <div className="space-y-1.5 border-t border-stone-200 pt-3 text-sm" data-testid="lightbox-faces">
                  <span className="text-stone-400 text-xs block">In this photo</span>
                  {facesOf(lightbox).filter((face) => face.status === 'approved').map((face) => (
                    <div key={face.face_index} className="flex items-center gap-2 text-stone-700">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />{face.contact?.name || 'A contact you cannot view'}
                    </div>
                  ))}
                  {hasFaceStatus(lightbox, 'to_review') && (
                    <button type="button" className="flex items-center gap-2 text-amber-700 hover:underline" onClick={() => reviewFace(facesOf(lightbox).find((face) => face.status === 'to_review'))}>
                      <Clock className="w-3.5 h-3.5" />{facesOf(lightbox).filter((face) => face.status === 'to_review').length} face(s) to review
                    </button>
                  )}
                </div>
              )}

              {/* DO NOT USE toggle */}
              <div className="pt-2 border-t border-stone-200">
                <button
                  onClick={() => toggleDoNotUse(lightbox)} disabled={!!readOnlyReason}
                  className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition ${
                    lightbox.do_not_use_marketing
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  <Ban className="w-4 h-4" />
                  {lightbox.do_not_use_marketing ? 'Marked: DO NOT USE' : 'Mark as DO NOT USE'}
                </button>
              </div>

              {/* Delete */}
              <button
                onClick={() => handleDelete(lightbox.photo_id)} disabled={!!readOnlyReason}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 transition"
              >
                <Trash2 className="w-4 h-4" />
                Delete photo
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
